import React, { useState, useEffect } from 'react';
import { getContactPageData } from '../services/contactService';
import { renderSafeText, getStrapiMedia } from '../lib/strapi';
import { MapPin, Send, Globe } from 'lucide-react';

const Contact = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const result = await getContactPageData();
      setData(result);
      setLoading(false);
    };
    fetchData();
  }, []);

  if (loading || !data) {
    return (
      <div className="py-40 text-center flex flex-col items-center justify-center space-y-4">
        <div className="w-12 h-12 border-4 border-widya-green border-t-transparent rounded-full animate-spin"></div>
        <p className="text-gray-400 font-bold uppercase tracking-widest text-[10px]">Loading Contact Details...</p>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* IMMERSIVE HERO SECTION */}
      <section 
        className="relative overflow-hidden min-h-[40vh] lg:min-h-[60vh] flex items-center justify-center bg-widya-footer bg-cover bg-center"
        style={{ backgroundImage: data.hero_image ? `url(${getStrapiMedia(data.hero_image)})` : 'none' }}
      >
        <div className="absolute inset-0 bg-black/50 z-0"></div>
        <div className="container mx-auto px-6 text-center relative z-10 w-full max-w-[1100px]">
          <h1 className="text-[40px] md:text-[56px] lg:text-[72px] font-bold tracking-tight leading-[1.1] max-w-4xl mx-auto font-heading text-white mb-6 drop-shadow-xl">
            {renderSafeText(data.hero_title)}
          </h1>
          <p className="text-[14px] md:text-[16px] text-widya-green font-black uppercase tracking-[0.3em] font-heading drop-shadow-md">
            {renderSafeText(data.hero_subtitle)}
          </p>
        </div>
      </section>

      <section className="py-24 px-6">
        <div className="container mx-auto max-w-[1100px] grid lg:grid-cols-2 gap-16">
          {/* CONTACT FORM */}
          <div className="bg-gray-50 p-8 md:p-12 rounded-[2.5rem] border border-gray-100">
            <h2 className="text-2xl font-bold text-widya-charcoal mb-8 flex items-center gap-2 font-heading">
              <Send className="text-widya-green" size={24} /> Send us a Message
            </h2>
            <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold uppercase text-gray-400 mb-2">Full Name</label>
                  <input type="text" className="w-full bg-white border border-gray-200 rounded-xl py-4 px-5 text-sm focus:ring-2 focus:ring-widya-green focus:border-transparent shadow-sm outline-none transition-all" placeholder="John Doe" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase text-gray-400 mb-2">Email Address</label>
                  <input type="email" className="w-full bg-white border border-gray-200 rounded-xl py-4 px-5 text-sm focus:ring-2 focus:ring-widya-green focus:border-transparent shadow-sm outline-none transition-all" placeholder="john@example.com" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase text-gray-400 mb-2">Company Name</label>
                <input type="text" className="w-full bg-white border border-gray-200 rounded-xl py-4 px-5 text-sm focus:ring-2 focus:ring-widya-green focus:border-transparent shadow-sm outline-none transition-all" placeholder="AgriCorp Ltd" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase text-gray-400 mb-2">Your Message</label>
                <textarea rows="5" className="w-full bg-white border border-gray-200 rounded-xl py-4 px-5 text-sm focus:ring-2 focus:ring-widya-green focus:border-transparent shadow-sm outline-none transition-all resize-none" placeholder="How can we help you?"></textarea>
              </div>
              <button type="submit" className="w-full bg-widya-green text-white font-bold py-4 rounded-xl hover:bg-widya-green-dark transition-all shadow-lg shadow-widya-green/20">
                Submit Inquiry
              </button>
            </form>
          </div>

          {/* OFFICE LOCATIONS */}
          <div className="space-y-12">
            <div>
              <h3 className="text-sm font-extrabold text-widya-green uppercase tracking-[0.2em] mb-4">Head Office</h3>
              <div className="flex items-start gap-4">
                <div className="p-3 bg-widya-green/10 text-widya-green rounded-lg shrink-0">
                  <Globe size={24} />
                </div>
                <div>
                  <p className="text-xl font-bold text-widya-charcoal mb-1">{renderSafeText(data.primary_contact_label)}</p>
                  <a href={`mailto:${data.primary_email}`} className="text-widya-body hover:text-widya-green transition-colors">
                    {renderSafeText(data.primary_email)}
                  </a>
                </div>
              </div>
            </div>

            <hr className="border-gray-100" />

            <div>
              <h3 className="text-sm font-extrabold text-gray-400 uppercase tracking-[0.2em] mb-8">International Presence</h3>
              <div className="grid sm:grid-cols-2 gap-8">
                {data.office_addresses?.map((office, i) => (
                  <div key={i} className="group">
                    <div className="flex items-center gap-2 mb-3 text-widya-charcoal font-bold">
                      <MapPin size={18} className="text-widya-green" />
                      {renderSafeText(office.city)}
                    </div>
                    <div className="text-sm text-widya-body leading-relaxed pl-7">
                      <p>{renderSafeText(office.address_line_1)}</p>
                      <p>{renderSafeText(office.address_line_2)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;